<template>
  <div class="login-container">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- 左侧品牌区 -->
    <div class="brand-section">
      <div class="brand-content">
        <div class="logo">
          <img :src="shopLogo" alt="logo" class="logo-img" />
          <span class="logo-text">{{ shopName }}</span>
        </div>
        <h1 class="brand-title">商户管理后台</h1>
        <p class="brand-desc">高效管理您的门店、商品和订单<br />数据驱动，智能运营</p>
        <div class="brand-features">
          <div class="feature-item">
            <el-icon>
              <Check />
            </el-icon>
            <span>实时数据看板</span>
          </div>
          <div class="feature-item">
            <el-icon>
              <Shop />
            </el-icon>
            <span>多门店管理</span>
          </div>
          <div class="feature-item">
            <el-icon>
              <Goods />
            </el-icon>
            <span>商品精细运营</span>
          </div>
          <div class="feature-item">
            <el-icon>
              <Document />
            </el-icon>
            <span>订单智能核销</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧登录区 - 居中显示 -->
    <div class="login-section">
      <div class="login-card">
        <div class="login-header">
          <h2 class="login-title">欢迎回来</h2>
          <p class="login-subtitle">请登录您的账号</p>
        </div>

        <el-form :model="loginForm" ref="loginFormRef" :rules="rules" class="login-form">
          <el-form-item prop="username">
            <el-input v-model="loginForm.username" placeholder="请输入账号" clearable size="large">
              <template #prefix>
                <el-icon>
                  <User />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item prop="password">
            <el-input v-model="loginForm.password" type="password" placeholder="请输入密码" show-password clearable
              size="large">
              <template #prefix>
                <el-icon>
                  <Lock />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleLogin" :loading="loading" class="login-btn" size="large">
              {{ loading ? '登录中...' : '立即登录' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <!-- 底部版权信息 - 全局底部 -->
    <div class="global-footer" v-if="copyrightList.length > 0">
      <div class="copyright-wrapper">
        <div v-for="(item, index) in copyrightList" :key="index" class="copyright-item">
          <a :href="item.value" target="_blank" rel="noopener noreferrer">{{ item.key }}</a>
          <span v-if="index < copyrightList.length - 1" class="copyright-separator">|</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import md5 from 'md5'
import Cookies from 'js-cookie'
import config from '@/config'
import { User, Lock, Check, Shop, Goods, Document } from '@element-plus/icons-vue'

export default {
  name: 'LoginPage',
  components: {
    User,
    Lock,
    Check,
    Shop,
    Goods,
    Document
  },
  setup() {
    const router = useRouter()
    const loginForm = ref({
      username: '',
      password: ''
    })
    const loading = ref(false)
    const loginFormRef = ref(null)
    const shopLogo = ref('@/assets/logo.png') // 默认logo
    const shopName = ref('商户端') // 默认名称
    const copyrightList = ref([]) // 版权信息列表

    const rules = {
      username: [
        { required: true, message: '请输入账号', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' }
      ]
    }

    const handleLogin = () => {
      loginFormRef.value.validate(async (valid) => {
        if (!valid) return

        loading.value = true
        try {
          const response = await axios.post(
            `${config.BASE_URL}/shop/api/login.php`,
            {
              account: loginForm.value.username,
              password: md5(loginForm.value.password)
            }
          )

          if (response.data.code === 200) {
            Cookies.set('user_token', response.data.data.id, { expires: 7 })
            Cookies.set('user_info', JSON.stringify(response.data.data), { expires: 7 })

            ElMessage.success(response.data.message)
            router.push('/dashboard')
          } else {
            const errorMessages = {
              400: '请输入完整的账号密码',
              401: '账号或密码错误',
              403: response.data.message,
              500: '服务器错误，请稍后再试'
            }
            ElMessage.error(errorMessages[response.data.code] || '登录失败')
          }
        } catch (error) {
          if (!error.response) {
            ElMessage.error('网络连接异常，请检查网络')
          } else {
            ElMessage.error('登录失败，请重试')
          }
        } finally {
          loading.value = false
        }
      })
    }

    // 加载店铺信息和版权信息
    const loadShopInfo = async () => {
      try {
        // 获取当前域名
        const currentDomain = window.location.hostname

        // 通过 URL 参数传递域名，而不是请求头
        const response = await axios.get(`${config.BASE_URL}/shop/api/web_icon.php`, {
          params: {
            domain: currentDomain
          }
        })

        if (response.data.success) {
          shopLogo.value = response.data.data.shop_logo || '@/assets/logo.gif'
          shopName.value = response.data.data.shop_name || '商户端'
          
          // 获取版权信息
          if (response.data.data.copyright && Array.isArray(response.data.data.copyright)) {
            copyrightList.value = response.data.data.copyright
          }
        }
      } catch (error) {
        console.error('加载店铺信息失败:', error)
        // 使用默认值
        shopLogo.value = '@/assets/logo.gif'
        shopName.value = '商户端'
        copyrightList.value = []
      }
    }

    onMounted(() => {
      loadShopInfo()
    })

    return {
      loginForm,
      rules,
      loginFormRef,
      loading,
      shopLogo,
      shopName,
      copyrightList,
      handleLogin
    }
  }
}
</script>

<style scoped lang="scss">
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
  padding: 0 40px;

  // 背景装饰
  .bg-decoration {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 0;

    .circle {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.08);
      animation: float 20s infinite ease-in-out;

      &.circle-1 {
        width: 400px;
        height: 400px;
        top: -150px;
        right: -100px;
        animation-delay: 0s;
      }

      &.circle-2 {
        width: 600px;
        height: 600px;
        bottom: -250px;
        left: -200px;
        animation-delay: -5s;
      }

      &.circle-3 {
        width: 250px;
        height: 250px;
        top: 40%;
        left: 20%;
        animation-delay: -10s;
      }
    }
  }

  @keyframes float {

    0%,
    100% {
      transform: translateY(0) scale(1);
    }

    50% {
      transform: translateY(-40px) scale(1.05);
    }
  }

  // 左侧品牌区
  .brand-section {
    flex: 1;
    max-width: 600px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 40px;
    position: relative;
    z-index: 1;

    .brand-content {
      width: 100%;
      color: #fff;

      .logo {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 40px;

        .logo-img {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: #fff;
          padding: 10px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .logo-text {
          font-size: 32px;
          font-weight: 700;
          letter-spacing: 2px;
        }
      }

      .brand-title {
        font-size: 42px;
        font-weight: 700;
        margin-bottom: 20px;
        line-height: 1.2;
        background: linear-gradient(135deg, #fff, rgba(255, 255, 255, 0.8));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .brand-desc {
        font-size: 16px;
        opacity: 0.9;
        margin-bottom: 48px;
        line-height: 1.6;
      }

      .brand-features {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;

        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
          background: rgba(255, 255, 255, 0.1);
          padding: 10px 16px;
          border-radius: 12px;
          backdrop-filter: blur(10px);

          .el-icon {
            font-size: 18px;
          }
        }
      }
    }
  }

  // 右侧登录区
  .login-section {
    width: 500px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    position: relative;
    z-index: 1;

    .login-card {
      width: 100%;
      background: #fff;
      border-radius: 32px;
      padding: 48px 44px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);

      .login-header {
        text-align: center;
        margin-bottom: 40px;

        .login-title {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 8px;
        }

        .login-subtitle {
          font-size: 14px;
          color: #8c8c8c;
        }
      }

      .login-form {
        :deep(.el-input__wrapper) {
          border-radius: 12px;
          padding: 8px 16px;
          box-shadow: 0 0 0 1px #e8e8e8 inset;
          transition: all 0.3s;

          &:hover {
            box-shadow: 0 0 0 1px #667eea inset;
          }

          &.is-focus {
            box-shadow: 0 0 0 2px #667eea inset;
          }
        }

        :deep(.el-input__prefix) {
          margin-right: 12px;
          color: #8c8c8c;
        }

        :deep(.el-form-item) {
          margin-bottom: 28px;
        }
      }

      .login-btn {
        width: 100%;
        height: 50px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 12px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        transition: all 0.3s;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }
      }
    }
  }

  // 全局底部版权信息
  .global-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 20px;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    z-index: 2;
    text-align: center;

    .copyright-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      .copyright-item {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.8);

        a {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: all 0.3s;

          &:hover {
            color: #fff;
            text-decoration: underline;
          }
        }

        .copyright-separator {
          margin: 0 8px;
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }
  }
}

// 平板适配
@media (max-width: 1280px) {
  .login-container {
    .brand-section {
      max-width: 500px;
      padding: 40px 20px;

      .brand-content {
        .brand-title {
          font-size: 36px;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .login-container {
    .brand-section {
      padding: 40px;

      .brand-content {
        .brand-title {
          font-size: 32px;
        }

        .brand-features {
          grid-template-columns: 1fr;
          gap: 12px;
        }
      }
    }

    .login-section {
      width: 450px;

      .login-card {
        padding: 40px 32px;
      }
    }
  }
}

// 移动端适配
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    padding: 0 20px;

    .brand-section {
      max-width: 100%;
      padding: 40px 0 20px;

      .brand-content {
        .logo {
          justify-content: center;
        }

        .brand-features {
          display: none;
        }

        .brand-desc {
          margin-bottom: 0;
        }
      }
    }

    .login-section {
      width: 100%;
      padding: 20px 0 60px;

      .login-card {
        padding: 36px 28px;
      }
    }

    .global-footer {
      padding: 12px 16px;

      .copyright-wrapper {
        .copyright-item {
          font-size: 10px;
        }
      }
    }
  }
}
</style>